import React, { useState, useEffect, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { FaGripVertical, FaLock, FaChevronDown, FaChevronUp } from 'react-icons/fa';

// Drag-and-drop subject reorder for day-wise create/edit schedule pages.
// Optional selection (allSubjects + onSelectionChange): checkboxes; only selected can reorder.

interface SubjectReorderPanelProps {
  mode: 'create' | 'edit';
  /** Selected subjects in order (also used as the full list when allSubjects is omitted). */
  subjects: string[];
  /** All course subject names. When set, unselected subjects appear below selected. */
  allSubjects?: string[];
  isSubjectLocked?: (name: string) => boolean;
  canUnselect?: (name: string) => boolean;
  onReorder: (newOrder: string[]) => void;
  onSelectionChange?: (selected: string[]) => void;
}

const SubjectReorderPanel: React.FC<SubjectReorderPanelProps> = ({
  mode,
  subjects,
  allSubjects,
  isSubjectLocked,
  canUnselect,
  onReorder,
  onSelectionChange,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [dragError, setDragError] = useState<string | null>(null);

  const selectionEnabled = !!allSubjects && !!onSelectionChange;
  const selected = subjects;
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const displayList = useMemo(() => {
    if (!selectionEnabled || !allSubjects) return selected;
    const unselected = allSubjects.filter((name) => !selectedSet.has(name));
    return [...selected, ...unselected];
  }, [selectionEnabled, allSubjects, selected, selectedSet]);

  const [localSubjects, setLocalSubjects] = useState<string[]>(displayList);

  useEffect(() => {
    setLocalSubjects(displayList);
  }, [displayList]);

  const selectedCount = selected.length;
  const locked = (name: string) => mode === 'edit' && (isSubjectLocked?.(name) ?? false);

  const showError = (msg: string) => {
    setDragError(msg);
    setTimeout(() => setDragError(null), 3000);
  };

  const handleToggle = (name: string) => {
    if (!onSelectionChange) return;
    if (selectedSet.has(name)) {
      if (canUnselect && !canUnselect(name)) {
        showError('Cannot unselect: start date is before today, or students have accessed this subject.');
        return;
      }
      onSelectionChange(selected.filter((n) => n !== name));
    } else {
      onSelectionChange([...selected, name]);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    let destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) return;

    // With selection UI, only the selected block (prefix) is reorderable
    if (selectionEnabled) {
      if (sourceIndex >= selectedCount) return;
      if (destinationIndex >= selectedCount) destinationIndex = selectedCount - 1;
      if (sourceIndex === destinationIndex) return;
    }

    const sourceName = localSubjects[sourceIndex];
    const destName = localSubjects[destinationIndex];

    setDragError(null);

    if (mode === 'edit') {
      if (locked(sourceName)) {
        showError('Cannot move locked subjects.');
        return;
      }
      if (locked(destName)) {
        showError('Cannot move subjects into locked positions.');
        return;
      }
    }

    const items = Array.from(localSubjects);
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);

    const newSelected = selectionEnabled ? items.slice(0, selectedCount) : items;

    if (mode === 'edit') {
      const lockedMoved = selected.some(
        (name, idx) => isSubjectLocked?.(name) && newSelected[idx] !== name
      );
      if (lockedMoved) {
        showError('Locked subjects cannot change position.');
        return;
      }
    }

    setLocalSubjects(items);
    onReorder(newSelected);
  };

  if (displayList.length === 0) return null;

  return (
    <div className="card border-primary mb-3" style={{ overflow: 'visible' }}>
      <div
        className="card-header d-flex justify-content-between align-items-center bg-light"
        role="button"
        onClick={() => setExpanded((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <span className="fw-semibold text-primary">
          {selectionEnabled ? 'Select & Reorder Subjects' : 'Reorder Subjects'}
        </span>
        {expanded ? <FaChevronUp /> : <FaChevronDown />}
      </div>
      {expanded && (
        <div className="card-body p-3" style={{ overflow: 'visible' }}>
          {dragError && (
            <div className="alert alert-danger py-2 mb-2" role="alert">
              {dragError}
            </div>
          )}
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="subjects-reorder">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} style={{ overflow: 'visible' }}>
                  {localSubjects.map((name, index) => {
                    const isSelected = !selectionEnabled || selectedSet.has(name);
                    const isLocked = isSelected && locked(name);
                    const dragDisabled = !isSelected || isLocked;
                    const checkboxDisabled =
                      selectionEnabled && isSelected && !(canUnselect?.(name) ?? true);

                    return (
                      <Draggable
                        key={`subject-${name}`}
                        draggableId={`subject-${name}`}
                        index={index}
                        isDragDisabled={dragDisabled}
                      >
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...(!dragDisabled ? dragProvided.dragHandleProps : {})}
                            className={`d-flex align-items-center border rounded mb-2 px-3 py-2 ${
                              !isSelected || isLocked ? 'bg-light opacity-75' : ''
                            }`}
                            style={{
                              ...dragProvided.draggableProps.style,
                              backgroundColor: snapshot.isDragging ? '#e3f2fd' : undefined,
                              cursor: dragDisabled ? 'default' : 'grab',
                            }}
                          >
                            {selectionEnabled && (
                              <input
                                type="checkbox"
                                className="form-check-input me-2"
                                checked={isSelected}
                                disabled={checkboxDisabled}
                                onChange={() => handleToggle(name)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                            {isLocked ? (
                              <span className="me-2 text-muted"><FaLock /></span>
                            ) : isSelected ? (
                              <span className="me-2 text-muted"><FaGripVertical /></span>
                            ) : (
                              <span className="me-2" style={{ width: '1rem' }} />
                            )}
                            <span className={`fw-medium ${!isSelected ? 'text-muted' : ''}`}>{name}</span>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
    </div>
  );
};

export default SubjectReorderPanel;
